import { Badge, Button, Card, CardContent, CardDescription, CardTitle } from '@auctisync/ui';
import { Layout } from '../../components/Layout';
import { PageHead } from '../../components/PageHead';

/**
 * Marketing homepage — segmented-horizontal hero.
 *
 * Per business/strategy/WHITESPACE_STRATEGY.md:
 *   Hero: "The auction platform built for what's next."
 *   4 segment cards: auction houses / facilities / liquidators / specialty
 *   Case study carousel anchor: DTA (seeded once Jim signs off on copy)
 */

type Segment = {
  slug: string;
  title: string;
  tagline: string;
  description: string;
  bullets: string[];
};

const SEGMENTS: Segment[] = [
  {
    slug: '/for-auction-houses',
    title: 'Auction Houses',
    tagline: 'Traditional auctioneers',
    description: 'Estate, antique, collectibles, and charity auctions.',
    bullets: ['Persistent inventory', 'Consignor statements', 'Print catalog', 'Staggered close'],
  },
  {
    slug: '/for-facilities',
    title: 'Facilities',
    tagline: 'Lien-law verticals',
    description: 'Storage units, auto repair, RV, tow yards, marina, pawn.',
    bullets: ['5-state rule engine', 'RPost + USPS API', 'Column.us ads', 'Redemption tracking'],
  },
  {
    slug: '/for-liquidators',
    title: 'Liquidators',
    tagline: 'Business liquidation',
    description: 'Industrial, restaurant, construction, office, bankruptcy.',
    bullets: [
      'Trustee-Grade add-on',
      'Chain-of-custody audit',
      'Court report generator',
      '7-yr retention',
    ],
  },
  {
    slug: '/for-specialty',
    title: 'Specialty',
    tagline: 'Live commerce & collectibles',
    description: 'TCG, watches, memorabilia, movie props, vintage.',
    bullets: ['Live webcast', 'Modern marketplace UX', 'Niche field templates', 'Mobile-first'],
  },
];

export function Home() {
  return (
    <Layout>
      <PageHead
        title="AuctiSync — The auction platform built for what's next"
        description="One platform, every kind of auction. Built for auction houses, storage facilities, liquidators, and specialty dealers."
        canonicalPath="/"
      />
      <Hero />
      <Segments />
      <Anchor />
      <RecentlyShipped />
      <PricingCta />
    </Layout>
  );
}

function Hero() {
  return (
    <section className="mx-auto max-w-6xl px-6 pt-20 pb-16 md:pt-32 md:pb-24">
      <Badge className="mb-6">Segmented horizontal auction platform</Badge>
      <h1
        className="font-heading max-w-4xl text-5xl leading-tight font-bold tracking-tight md:text-7xl md:leading-[1.05]"
        style={{ color: 'var(--text)', letterSpacing: '-0.02em', textWrap: 'balance' }}
      >
        The auction platform built for what&apos;s next.
      </h1>
      <p className="mt-6 max-w-2xl text-lg md:text-xl" style={{ color: 'var(--text-muted)' }}>
        One platform for every kind of auction — from estate sales to storage-unit lien auctions,
        from business liquidation to live-commerce specialty. Modern infrastructure the legacy
        incumbents can't match.
      </p>
      <div className="mt-10 flex flex-wrap gap-4">
        <Button asChild size="lg">
          <a href="/contact">Request a demo</a>
        </Button>
        <Button asChild variant="outline" size="lg">
          <a href="/pricing">See pricing</a>
        </Button>
      </div>
    </section>
  );
}

function Segments() {
  return (
    <section
      className="border-y py-20"
      style={{ borderColor: 'var(--border)', background: 'var(--surface-muted)' }}
    >
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-12 flex items-end justify-between gap-6">
          <div>
            <h2
              className="font-heading text-3xl font-bold tracking-tight md:text-4xl"
              style={{ color: 'var(--text)' }}
            >
              Four segments, one platform
            </h2>
            <p className="mt-3 max-w-2xl" style={{ color: 'var(--text-muted)' }}>
              Pick the flavor of auction you run. We handle the compliance, bidding engine,
              payments, and marketplace liquidity underneath.
            </p>
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {SEGMENTS.map((segment) => (
            <a
              key={segment.slug}
              href={segment.slug}
              className="group transition-transform hover:-translate-y-0.5"
            >
              <Card className="h-full">
                <CardContent className="flex h-full flex-col gap-4 p-6">
                  <Badge variant="secondary" className="w-fit">
                    {segment.tagline}
                  </Badge>
                  <CardTitle className="text-xl">{segment.title}</CardTitle>
                  <CardDescription>{segment.description}</CardDescription>
                  <ul
                    className="mt-auto space-y-1.5 text-sm"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    {segment.bullets.map((bullet) => (
                      <li key={bullet} className="flex gap-2">
                        <span aria-hidden>→</span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function Anchor() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-20">
      <div className="grid gap-10 md:grid-cols-[1fr_2fr] md:items-center">
        <div>
          <Badge variant="success" className="mb-4">
            Anchor customer
          </Badge>
          <h2
            className="font-heading text-3xl font-bold tracking-tight md:text-4xl"
            style={{ color: 'var(--text)' }}
          >
            Design Partner: Discount Tool Auctions
          </h2>
        </div>
        <div>
          <p className="text-lg" style={{ color: 'var(--text-muted)' }}>
            A full-service weekly auction house replacing a decade-old legacy system with AuctiSync.
            Running thousands of lots per week across estate, consignment, and business liquidation
            — with zero disruption and a modern bidder experience.
          </p>
          <p className="mt-4">
            <a
              href="/case-studies/dta"
              className="text-sm font-medium underline-offset-4 hover:underline"
              style={{ color: 'var(--brand-accent)' }}
            >
              Read the case study →
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}

function RecentlyShipped() {
  const items: Array<{ title: string; body: string; href: string; date: string }> = [
    {
      title: 'Web Push notifications',
      body: 'Outbid, ending-soon, and win alerts arrive on bidders’ phones and laptops — even when the site is closed. Standards-based VAPID; works on iOS Safari PWA + Chrome / Firefox / Edge.',
      href: 'https://help.auctisync.com/a/push-notifications-overview',
      date: '2026-05-18',
    },
    {
      title: 'AI Help Assistant',
      body: 'Bidders and auction-house staff ask plain-language questions; the assistant answers from the 88-article help corpus with grounded citations. Audience-scoped so bidders don’t see internal SOPs.',
      href: 'https://help.auctisync.com/ask',
      date: '2026-05-12',
    },
    {
      title: 'Auctioneer software integration',
      body: 'External live-auction software signs requests with HMAC-SHA256; AuctiSync’s engine verifies + records the bid under the floor bidder, with the auctioneer staff attribution on the chain. Per-tenant key generation in the admin UI; secrets in the database, not in deploy configs.',
      href: 'https://help.auctisync.com/a/auctioneer-hmac-keys',
      date: '2026-05-18',
    },
    {
      title: 'Bidder CRM',
      body: 'LTV view, custom tags, saved segments, CSV export, segment-driven broadcasts. Built on the bidder-lifetime-stats view that runs across invoices, payments, and bids — not stale denormalized columns.',
      href: 'https://help.auctisync.com/',
      date: '2026-05-11',
    },
    {
      title: 'Auction Engine v1',
      body: 'Cloudflare Workers + Durable Objects + Postgres write-behind. Sub-100ms bid latency, three-tier write path with R2 catastrophic overflow, 12-alert observability layer. The engine the legacy incumbents won’t build for another decade.',
      href: '/features/bidding-engine',
      date: '2026-04',
    },
  ];

  return (
    <section
      className="border-t py-20"
      style={{ borderColor: 'var(--border)', background: 'var(--surface-muted)' }}
    >
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-10">
          <Badge variant="secondary" className="mb-4">
            Recently shipped
          </Badge>
          <h2
            className="font-heading text-3xl font-bold tracking-tight md:text-4xl"
            style={{ color: 'var(--text)' }}
          >
            We ship in weeks, not quarters.
          </h2>
          <p className="mt-3 max-w-2xl" style={{ color: 'var(--text-muted)' }}>
            A running log of what landed in production recently. The legacy auction-software
            incumbents take years to ship features that fit on a single page.
          </p>
        </div>
        <ul className="grid gap-4 md:grid-cols-2">
          {items.map((item) => (
            <li key={item.title}>
              <a
                href={item.href}
                className="group block h-full transition-transform hover:-translate-y-0.5"
              >
                <Card className="h-full">
                  <CardContent className="flex h-full flex-col gap-3 p-6">
                    <div
                      className="text-xs font-medium tracking-wider uppercase"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      {item.date}
                    </div>
                    <CardTitle className="text-lg">{item.title}</CardTitle>
                    <CardDescription>{item.body}</CardDescription>
                    <span
                      className="mt-auto text-sm font-medium underline-offset-4 group-hover:underline"
                      style={{ color: 'var(--brand-accent)' }}
                    >
                      Read more &rarr;
                    </span>
                  </CardContent>
                </Card>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function PricingCta() {
  return (
    <section
      className="border-t py-20"
      style={{ borderColor: 'var(--border)', background: 'var(--surface-inverted)' }}
    >
      <div className="mx-auto max-w-4xl px-6 text-center">
        <h2
          className="text-3xl font-bold tracking-tight md:text-4xl"
          style={{ color: 'var(--text-on-inverted)' }}
        >
          Nine tiers. One platform. Zero legacy baggage.
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-lg" style={{ color: 'rgba(255,255,255,0.7)' }}>
          From Solo ($49/mo) to Enterprise Plus. Trustee-Grade add-on available on Growth+.
        </p>
        <div className="mt-8 flex justify-center gap-4">
          <Button asChild size="lg">
            <a href="/pricing">See pricing</a>
          </Button>
        </div>
      </div>
    </section>
  );
}
